import { findingComponentIdentity, type ComponentIdentities } from "./component-identity.ts";
export type IntakePart = { description?: string; quantity?: string; source?: string; suggestedSourceType?: "in-house-catalog" | "external-supplier" | "manual-review"; suggestedSourceName?: string };
export type IntakeFinding = { classification?: string; sourceRows?: number[]; sourceText?: string; proposedScope?: string; confidence?: string; manHours?: number | null; manHoursSource?: string; parts?: IntakePart[]; partsReviewRequired?: boolean };
export type IntakeAsset = { componentIdentities?: ComponentIdentities; sourceName?: string; sourceIdentifier?: string; reportNumber?: string; proposedAssetMatch?: string; matchStatus?: string; hoistManufacturer?: string; modelNumber?: string; serialNumber?: string; findings?: IntakeFinding[] };
export type IntakeResult = { projectNumber?: string; assets?: IntakeAsset[] };

export type PartsSourceType = "unassigned" | "in-house" | "supplier";
export type ReviewPart = { id: string; description: string; quantity: string; source: string; unitSell: string; unitCost?: string; markupPercent?: string; sourceType?: PartsSourceType; sourceConfirmed?: boolean; supplierId?: string; catalogItemId?: string; suggestedSourceType?: IntakePart["suggestedSourceType"]; suggestedSourceName?: string };
export type ReviewHoldKind = "" | "identity" | "policy";
export type ReviewIdentity = { manufacturer: string; modelNumber: string; serialNumber: string };
export type IdentityCorrection = { previous: ReviewIdentity; corrected: ReviewIdentity; previousComponent?: string; component?: string; note: string; at: string };
export type ReviewFinding = {
  originalIdentity?: ReviewIdentity;
  identityCorrections?: IdentityCorrection[];
  id: string;
  assetIndex: number;
  findingIndex: number;
  assetName: string;
  assetId: string;
  reportNumber: string;
  manufacturer: string;
  modelNumber: string;
  serialNumber: string;
  matchStatus: string;
  component?: string;
  identityEvidence?: string;
  classification: string;
  sourceRows: number[];
  sourceText: string;
  scope: string;
  manHours: string;
  manHoursSource: string;
  parts: ReviewPart[];
  partsReviewRequired?: boolean;
  noPartsConfirmed?: boolean;
  decision: "estimate" | "clarification" | "excluded" | "hold";
  include: boolean;
  evidenceReviewed: boolean;
  holdReason: string;
  holdKind?: ReviewHoldKind;
  derivedFromFindingId?: string;
};
export type ReviewReadinessIssue = { findingId: string; assetId: string; sourceRows: number[]; reasons: string[] };

export type ReviewPackage = { projectNumber: string; findings: ReviewFinding[] };
export type EstimateLine = { id: string; assetId: string; category: "Labour" | "Parts"; description: string; quantity: string; unit: string; source: string; unitSellCents: number | null; amountCents: number | null; origin: "reviewed" | "manual" };
export type EstimateDraft = { projectNumber: string; lines: EstimateLine[]; knownSubtotalCents: number; incompletePricing: boolean; heldFindings: number };
export type AssetEstimateSummary = { assetId: string; totalCents: number; lines: EstimateLine[] };

const holdClassifications = new Set(["engineering-review", "safety-context", "missing-documentation", "work-already-recorded"]);

export function createReviewPackage(result: IntakeResult): ReviewPackage {
  return {
    projectNumber: result.projectNumber?.trim() || "Confirmation required",
    findings: (result.assets ?? []).flatMap((asset, assetIndex) => (asset.findings ?? []).map((finding, findingIndex) => {
      const component = findingComponentIdentity(finding, asset.componentIdentities);
      const classification = finding.classification || "unclassified";
      const identityResolved = asset.matchStatus === "exact" && (!component || Boolean(component.manufacturer && component.model && component.serial));
      const holdKind: ReviewHoldKind = holdClassifications.has(classification) ? "policy" : identityResolved ? "" : "identity";
      const holdReason = holdKind === "policy"
        ? "This classification stays outside automatic repair pricing."
        : holdKind === "identity" ? "Confirm the asset identity before including this repair." : "";
      return {
        id: `A${assetIndex + 1}-F${findingIndex + 1}`,
        assetIndex,
        findingIndex,
        assetName: asset.sourceName || "Unnamed equipment block",
        assetId: asset.sourceIdentifier || "Unresolved asset",
        reportNumber: asset.reportNumber || "Report confirmation required",
        component: component?.component ?? "",
        identityEvidence: component?.evidence ?? "",
        manufacturer: (component ? component.manufacturer : asset.hoistManufacturer) || "Manufacturer confirmation required",
        modelNumber: (component ? component.model : asset.modelNumber) || "Model confirmation required",
        serialNumber: (component ? component.serial : asset.serialNumber) || "Serial confirmation required",
        matchStatus: asset.matchStatus || "unresolved",
        classification,
        sourceRows: Array.isArray(finding.sourceRows) ? [...finding.sourceRows] : [],
        sourceText: finding.sourceText || "No source summary returned",
        scope: finding.proposedScope || "",
        manHours: typeof finding.manHours === "number" && Number.isFinite(finding.manHours) && finding.manHours >= 0 ? String(finding.manHours) : "",
        manHoursSource: finding.manHoursSource || "manual-required",
        parts: (finding.parts ?? []).filter((part) => part.description?.trim()).map((part, partIndex) => ({
          id: `A${assetIndex + 1}-F${findingIndex + 1}-P${partIndex + 1}`,
          description: part.description?.trim() || "",
          quantity: part.quantity?.trim() || "",
          source: part.source?.trim() || "source review",
          unitSell: "",
          unitCost: "",
          markupPercent: "",
          sourceType: "unassigned",
          sourceConfirmed: false,
          supplierId: "",
          catalogItemId: "",
          suggestedSourceType: part.suggestedSourceType,
          suggestedSourceName: part.suggestedSourceName?.trim() || "",
        })),
        partsReviewRequired: finding.partsReviewRequired ?? false,
        noPartsConfirmed: false,
        decision: holdReason ? "hold" : (classification === "repair-required" || classification === "failed-check") ? "estimate" : "clarification",
        include: !holdReason && (classification === "repair-required" || classification === "failed-check"),
        evidenceReviewed: false,
        holdReason,
        holdKind,
      };
    })),
  };
}

export function reviewHoldKind(finding: ReviewFinding): ReviewHoldKind {
  if (finding.holdKind) return finding.holdKind;
  if (!finding.holdReason) return "";
  return finding.holdReason.startsWith("Confirm the asset identity") ? "identity" : "policy";
}

export function confirmReviewAssetIdentity(review: ReviewPackage, assetIndex: number): ReviewPackage {
  return {
    ...review,
    findings: review.findings.map((finding) => {
      if (finding.assetIndex !== assetIndex || reviewHoldKind(finding) !== "identity") return finding;
      const estimate = finding.classification === "repair-required" || finding.classification === "failed-check";
      return {
        ...finding,
        matchStatus: "human-confirmed",
        holdReason: "",
        holdKind: "",
        decision: estimate ? "estimate" : "clarification",
        include: estimate,
      };
    }),
  };
}

export function correctReviewFindingIdentity(review: ReviewPackage, findingId: string, input: ReviewIdentity & { component?: string }, note: string, at: string): ReviewPackage {
  const corrected = { manufacturer: input.manufacturer.trim(), modelNumber: input.modelNumber.trim(), serialNumber: input.serialNumber.trim() };
  if (Object.values(corrected).some((value) => !value || value.length > 200 || /confirmation required|^(?:unknown|confirm|n\/?a|not stated|-)$|[\r\n]/i.test(value)))
    throw new Error("Enter a confirmed manufacturer, model and serial before saving the identity.");
  if (note.trim().length > 500 || !Number.isFinite(Date.parse(at))) throw new Error("Enter a valid correction note and time.");
  if (input.component !== undefined && !["Hoist", "Trolley", "Integrated trolley / hoist", "Other equipment"].includes(input.component)) throw new Error("Choose the component this repair belongs to before confirming its identity.");
  if (!review.findings.some((finding) => finding.id === findingId)) throw new Error("Finding not found.");
  return { ...review, findings: review.findings.map((finding) => {
    if (finding.id !== findingId) return finding;
    const previous = { manufacturer: finding.manufacturer, modelNumber: finding.modelNumber, serialNumber: finding.serialNumber };
    const component = input.component ?? finding.component ?? "";
    const changed = component !== (finding.component ?? "") || Object.keys(corrected).some((field) => previous[field as keyof ReviewIdentity] !== corrected[field as keyof ReviewIdentity]);
    const identityHold = reviewHoldKind(finding) === "identity";
    const repair = finding.classification === "repair-required" || finding.classification === "failed-check";
    return { ...finding, ...corrected, component, originalIdentity: finding.originalIdentity ?? previous,
      identityCorrections: [...(finding.identityCorrections ?? []), { previous, corrected, previousComponent: finding.component ?? "", component, note: note.trim() || "Identity confirmed by estimator during findings review.", at }],
      matchStatus: "human-confirmed", evidenceReviewed: false, noPartsConfirmed: changed ? false : finding.noPartsConfirmed ?? false,
      holdKind: identityHold ? "" : finding.holdKind ?? "", holdReason: identityHold ? "" : finding.holdReason,
      decision: identityHold ? repair ? "estimate" : "clarification" : finding.decision,
      include: identityHold ? repair : finding.include,
      // A changed identity must not retain supplier/catalog compatibility or pricing approval.
      parts: changed ? finding.parts.map((part) => ({ ...part, sourceConfirmed: false, supplierId: "", catalogItemId: "", unitCost: "", unitSell: "", markupPercent: "" })) : finding.parts,
    };
  }) };
}

const confirmedComponents = new Set(["Hoist", "Trolley", "Integrated trolley / hoist", "Other equipment"]);
const identityKnown = (value: string) => Boolean(value.trim()) && !/confirmation required|^(?:unknown|confirm|n\/?a|not stated|-)$|[\r\n]/i.test(value.trim());
const sameEquipmentReport = (a: ReviewFinding, b: ReviewFinding) => a.assetIndex === b.assetIndex && a.assetId === b.assetId && a.reportNumber === b.reportNumber;

/** Reuse only explicit human confirmations within this equipment/report, never another asset. */
export function savedReviewComponentIdentities(review: ReviewPackage, findingId: string): ReviewFinding[] {
  const target = review.findings.find((finding) => finding.id === findingId);
  if (!target) return [];
  const identities = new Map<string, ReviewFinding>();
  for (const finding of review.findings) {
    if (!sameEquipmentReport(target, finding) || finding.matchStatus !== "human-confirmed" || !confirmedComponents.has(finding.component ?? "") || ![finding.manufacturer, finding.modelNumber, finding.serialNumber].every(identityKnown)) continue;
    identities.set(JSON.stringify([finding.component, finding.manufacturer, finding.modelNumber, finding.serialNumber]), finding);
  }
  return [...identities.values()];
}

/** Explicit bulk confirmation never guesses the component or replaces a conflicting identity. */
export function confirmMatchingReviewComponentIdentities(review: ReviewPackage, sourceId: string, at: string): { review: ReviewPackage; updated: number } {
  const source = review.findings.find((finding) => finding.id === sourceId);
  if (!source || source.matchStatus !== "human-confirmed" || !confirmedComponents.has(source.component ?? "") || ![source.manufacturer, source.modelNumber, source.serialNumber].every(identityKnown)) throw new Error("Save and confirm this component identity first.");
  const fields = ["manufacturer", "modelNumber", "serialNumber"] as const;
  const targets = review.findings.filter((finding) => finding.id !== source.id && sameEquipmentReport(source, finding)
    && finding.component === source.component && finding.matchStatus !== "human-confirmed"
    && fields.every((field) => !identityKnown(finding[field]) || finding[field].trim() === source[field].trim()));
  let result = review;
  for (const target of targets) result = correctReviewFindingIdentity(result, target.id, { manufacturer: source.manufacturer, modelNumber: source.modelNumber, serialNumber: source.serialNumber, component: source.component ?? "" }, `Reused confirmed component identity from ${source.id} in this equipment/report.`, at);
  return { review: result, updated: targets.length };
}

export function canCreateManualPricingFinding(finding: ReviewFinding) {
  return reviewHoldKind(finding) === "policy"
    && ["engineering-review", "missing-documentation", "safety-context"].includes(finding.classification);
}

export function createManualPricingFinding(review: ReviewPackage, findingId: string): ReviewPackage {
  const source = review.findings.find((finding) => finding.id === findingId);
  if (!source || !canCreateManualPricingFinding(source) || review.findings.some((finding) => finding.derivedFromFindingId === findingId)) return review;
  const manual: ReviewFinding = {
    ...source,
    id: `${source.id}-manual`,
    classification: "manual-commercial-item",
    scope: source.scope,
    manHours: source.classification === "missing-documentation" ? source.manHours : "",
    manHoursSource: "manual-required",
    parts: source.parts.map((part, index) => ({ ...part, id: `${source.id}-manual-P${index + 1}`, sourceConfirmed: false, supplierId: "", catalogItemId: "", unitCost: "", unitSell: "", markupPercent: "" })),
    decision: "estimate",
    include: true,
    evidenceReviewed: false,
    holdReason: "",
    holdKind: "",
    derivedFromFindingId: source.id,
  };
  return { ...review, findings: [...review.findings, manual] };
}

function decimalToThousandths(value: string) {
  const trimmed = value.trim();
  if (!/^\d+(?:\.\d{1,3})?$/.test(trimmed)) return null;
  const [whole = "0", fraction = ""] = trimmed.split(".");
  const result = Number.parseInt(whole, 10) * 1000 + Number.parseInt(fraction.padEnd(3, "0"), 10);
  return Number.isSafeInteger(result) ? result : null;
}

function quantityToThousandths(value: string) {
  const match = /^(\d+(?:\.\d{1,3})?)\s*(?:ea|each|unit|units)?$/i.exec(value.trim());
  return match ? decimalToThousandths(match[1] ?? "") : null;
}

export function dollarsToCents(value: string) {
  const trimmed = value.trim().replace(/^\$/, "").replaceAll(",", "");
  if (!/^\d+(?:\.\d{1,2})?$/.test(trimmed)) return null;
  const [whole = "0", fraction = ""] = trimmed.split(".");
  const result = Number.parseInt(whole, 10) * 100 + Number.parseInt(fraction.padEnd(2, "0"), 10);
  return Number.isSafeInteger(result) ? result : null;
}

function multiplyCents(cents: number, quantity: string) {
  const thousandths = quantityToThousandths(quantity);
  if (thousandths === null) return null;
  return Math.floor((cents * thousandths + 500) / 1000);
}

export function calculateLineAmount(unitSellCents: number | null, quantity: string) {
  return unitSellCents === null ? null : multiplyCents(unitSellCents, quantity);
}

export function findingCanEnterEstimate(finding: ReviewFinding) {
  return reviewFindingReadinessReasons(finding).length === 0;
}

export function reviewFindingReadinessReasons(finding: ReviewFinding) {
  if (finding.decision !== "estimate" || !finding.include) return [];
  const reasons: string[] = [];
  if (finding.matchStatus !== "exact" && finding.matchStatus !== "human-confirmed") reasons.push("confirmed asset identity");
  if (!finding.evidenceReviewed) reasons.push("source evidence review");
  if (finding.holdReason) reasons.push("resolved hold");
  if (!finding.scope.trim()) reasons.push("repair scope");
  if (decimalToThousandths(finding.manHours) === null) reasons.push("valid total man-hours");
  if (finding.partsReviewRequired && finding.parts.length === 0 && !finding.noPartsConfirmed) reasons.push("parts requirement review");
  if (!finding.parts.every((part) => part.description.trim().length > 0 && quantityToThousandths(part.quantity || "1") !== null && (!part.source.startsWith("scope-derived") || part.quantity.trim().length > 0))) reasons.push("valid part description and quantity");
  if (!finding.parts.every((part) => part.sourceConfirmed && ((part.sourceType === "in-house" && Boolean(part.catalogItemId)) || (part.sourceType === "supplier" && Boolean(part.supplierId))))) reasons.push("confirmed parts source");
  return reasons;
}

export function reviewReadinessIssues(review: ReviewPackage): ReviewReadinessIssue[] {
  return review.findings.flatMap((finding) => {
    const reasons = reviewFindingReadinessReasons(finding);
    return reasons.length ? [{ findingId: finding.id, assetId: finding.assetId, sourceRows: finding.sourceRows, reasons }] : [];
  });
}

export function reviewPackageReady(review: ReviewPackage) {
  return review.findings.some((finding) => finding.decision === "estimate" && finding.include) && reviewReadinessIssues(review).length === 0;
}

export function buildEstimateDraft(review: ReviewPackage, labourRate: string): EstimateDraft {
  if (!reviewPackageReady(review)) throw new Error("Every included repair requires reviewed evidence, confirmed identity, scope and valid man-hours.");
  const labourRateCents = dollarsToCents(labourRate);
  if (labourRateCents === null) throw new Error("Enter a valid non-negative labour sell rate.");
  const lines: EstimateLine[] = [];
  for (const finding of review.findings.filter((item) => item.decision === "estimate" && item.include)) {
    lines.push({
      id: `${finding.id}-L`, assetId: finding.assetId, category: "Labour", description: finding.scope.trim(), quantity: finding.manHours,
      unit: "hr", source: `${finding.reportNumber} · reviewed source rows ${finding.sourceRows.join(", ") || "not stated"}`,
      unitSellCents: labourRateCents, amountCents: multiplyCents(labourRateCents, finding.manHours), origin: "reviewed",
    });
    for (const part of finding.parts) {
      const unitSellCents = dollarsToCents(part.unitSell);
      lines.push({
        id: part.id, assetId: finding.assetId, category: "Parts", description: part.description, quantity: part.quantity || "1",
        unit: "ea", source: `${finding.reportNumber} · ${part.source}`, unitSellCents, amountCents: unitSellCents === null ? null : multiplyCents(unitSellCents, part.quantity || "1"), origin: "reviewed",
      });
    }
  }
  return {
    projectNumber: review.projectNumber,
    lines,
    knownSubtotalCents: lines.reduce((total, line) => total + (line.amountCents ?? 0), 0),
    incompletePricing: lines.some((line) => line.amountCents === null),
    heldFindings: review.findings.filter((finding) => !finding.include).length,
  };
}

export function recalculateEstimateDraft(estimate: EstimateDraft): EstimateDraft {
  const lines = estimate.lines.map((line) => ({ ...line, amountCents: calculateLineAmount(line.unitSellCents, line.quantity) }));
  return {
    ...estimate,
    lines,
    knownSubtotalCents: lines.reduce((total, line) => total + (line.amountCents ?? 0), 0),
    incompletePricing: lines.some((line) => line.amountCents === null || line.description.trim().length === 0 || quantityToThousandths(line.quantity) === null),
  };
}

export function summarizeEstimateByAsset(estimate: EstimateDraft): AssetEstimateSummary[] {
  const groups = new Map<string, AssetEstimateSummary>();
  for (const line of estimate.lines) {
    const assetId = line.assetId.trim() || "Project";
    const group = groups.get(assetId) ?? { assetId, totalCents: 0, lines: [] };
    group.lines.push({ ...line });
    group.totalCents += line.amountCents ?? 0;
    if (!Number.isSafeInteger(group.totalCents)) throw new Error("The asset estimate total is too large.");
    groups.set(assetId, group);
  }
  return [...groups.values()];
}

export function createManualEstimateLine(id: string, category: "Labour" | "Parts"): EstimateLine {
  return { id, assetId: "Project", category, description: "", quantity: "1", unit: category === "Labour" ? "hr" : "ea", source: "Manual estimate line", unitSellCents: null, amountCents: null, origin: "manual" };
}

export function addProjectCosts(estimate: EstimateDraft, costs: Record<string, string>) {
  const values = Object.values(costs).map(dollarsToCents);
  return {
    knownTotalCents: estimate.knownSubtotalCents + values.reduce<number>((total, value) => total + (value ?? 0), 0),
    incompletePricing: estimate.incompletePricing || values.some((value) => value === null),
  };
}

export function formatCad(cents: number) {
  return new Intl.NumberFormat("en-CA", { style: "currency", currency: "CAD" }).format(cents / 100);
}
